import { handleErrorAxios } from "@/common/lib/formatter";
import Http from "@/common/lib/http";
import { formatReference } from "@/common/lib/reference";

const SALE_PRODUCTS_CACHE_TTL_MS = 5 * 60 * 1000;

// The backend parses ranges with qs bracket notation (stock[start]=5),
// but Http.flattenParams flattens nested objects with dot notation,
// so the bracket keys are built here explicitly.
const buildRangeParams = (name, range) => {
    if (!range) return {};
    const params = {};
    if (range.start !== undefined) params[`${name}[start]`] = range.start;
    if (range.end !== undefined) params[`${name}[end]`] = range.end;
    return params;
};

// Toda escritura de productos borra la vista previa cacheada del inicio
// (tenants/catalogPreview), que muestra la primera página de productos.
const actions = {
    async verifyMassiveProductsAndInventories(
        { commit, dispatch },
        { merchantId, merchantType, rows }
    ) {
        try {
            const { data } = await Http.instance.post(
                "/bo/products/massive/verify-with-inventories",
                {
                    rows,
                    merchantId,
                    merchantType
                }
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async saveMassiveProductsAndInventories(
        { commit, dispatch },
        { merchantId, merchantType, productsAndInventories }
    ) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            const { data } = await Http.instance.post(
                "/bo/products/massive/save-with-inventories",
                {
                    merchantId,
                    merchantType,
                    productsAndInventories
                }
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    /**
     * 
     * export interface MassiveListProductsItemResponseModel {
    name: string;
    sku: string;
    barcode: string;
    price: number;
    priceOffer: number;
    stock: number;
    size?: string;
    color?: string;
    cost?: number;
    brandSlug?: string;
    lineSlug?: string;
    summary?: string;
    colorRef?: string;
}

export interface MassiveListProductsResponseModel {
    items: MassiveListProductsItemResponseModel[];
}
     */
    async listMassiveProducts(
        { commit, dispatch },
        { active, priceOffer, stock } = {}
    ) {
        try {
            const { data } = await Http.instance.get("/bo/products/massive", {
                params: {
                    active,
                    ...buildRangeParams("stock", stock),
                    ...buildRangeParams("priceOffer", priceOffer)
                }
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async verifyMassiveProducts({ commit, dispatch }, { rows }) {
        try {
            const { data } = await Http.instance.post(
                "/bo/products/massive/verify",
                {
                    rows
                }
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async saveMassiveProducts(
        { commit, dispatch },
        { needUpdateProducts, products }
    ) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            const { data } = await Http.instance.post(
                "/bo/products/massive/save",
                {
                    products,
                    needUpdateProducts
                }
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async verifyMassiveReferencesPrices({ commit, dispatch }, { rows }) {
        try {
            const { data } = await Http.instance.post(
                "/bo/products/massive/prices/verify",
                { rows }
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async saveMassiveReferencesPrices({ commit, dispatch }, { references }) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            const { data } = await Http.instance.post(
                "/bo/products/massive/prices/save",
                { references }
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async createProduct(
        { commit, dispatch },
        {
            brand,
            content,
            deliveryOptions,
            features,
            keywords,
            madeIn,
            name,
            sku,
            slug,
            summary,
            tags,
            targetGroups,
            type
        }
    ) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            const { data } = await Http.instance.post("/bo/products", {
                name,
                sku,
                type,
                slug,
                tags,
                madeIn,
                targetGroups,
                keywords,
                features,
                content,
                deliveryOptions,
                summary,
                brandSlug: brand.slug
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },

    async recordProduct(
        { commit, dispatch },
        {
            active,
            brandSlug,
            customReferenceTitle,
            images,
            lineSlug,
            merchantId,
            merchantType,
            name,
            price,
            references,
            sku,
            stock,
            type
        }
    ) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            const { data } = await Http.instance.post("/bo/products/record", {
                active,
                brandSlug,
                customReferenceTitle,
                images,
                lineSlug,
                merchantId,
                merchantType,
                name,
                price,
                references,
                sku,
                stock,
                type
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async listProductsPaginated(
        { commit, dispatch },
        {
            active,
            barcode,
            brandSlug,
            complete,
            itemsByPage,
            keyword,
            lineSlug,
            orderBy,
            orderType,
            page,
            priceOffer,
            sku,
            stock
        }
    ) {
        try {
            const { data } = await Http.instance.get("/bo/products", {
                params: {
                    active,
                    barcode,
                    brandSlug,
                    complete,
                    keyword,
                    lineSlug,
                    orderBy,
                    orderType,
                    page,
                    itemsByPage,
                    sku,
                    ...buildRangeParams("stock", stock),
                    ...buildRangeParams("priceOffer", priceOffer)
                }
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async listAllProducts(
        { commit, dispatch },
        { active, brandSlug, keyword, lineSlug }
    ) {
        try {
            const { data } = await Http.instance.get("/bo/products/all", {
                params: { active, lineSlug, brandSlug, keyword }
            });
            return data.items;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async ensureSaleProducts({ commit, dispatch, getters, rootState, state }) {
        if (getters.isSaleProductsCacheValid) {
            return state.saleProducts.data;
        }
        const data = await dispatch("listAllProducts", { active: true });
        commit("SET_SALE_PRODUCTS", {
            data,
            tenantId: rootState.auth.tenantId,
            expiredAt: Date.now() + SALE_PRODUCTS_CACHE_TTL_MS
        });
        return data;
    },
    async getProductById({ commit, dispatch }, { id }) {
        try {
            const { data } = await Http.instance.get(`/bo/products/${id}`);
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async getProductByBarcode({ commit, dispatch }, { barcode }) {
        try {
            const { data } = await Http.instance.get(
                `/bo/products/barcode/${barcode}`
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async updateProduct(
        { commit, dispatch },
        {
            brand,
            content,
            customReferenceTitle,
            features,
            id,
            keywords,
            madeIn,
            name,
            sku,
            slug,
            summary,
            tags,
            targetGroups,
            taxRate,
            type,
            weight
        }
    ) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            const { data } = await Http.instance.patch(`/bo/products/${id}`, {
                ...(brand ? { brandSlug: brand.slug } : {}),
                content,
                features,
                targetGroups,
                keywords,
                madeIn,
                name,
                summary,
                sku,
                slug,
                tags,
                taxRate,
                type,
                weight,
                customReferenceTitle
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async updateProductExtraOptions(
        { commit, dispatch },
        { extraOptions, id }
    ) {
        try {
            const { data } = await Http.instance.patch(`/bo/products/${id}`, {
                extraOptions
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async updateProductDelivery(
        { commit, dispatch },
        { id, logisticConfigurationSlug }
    ) {
        try {
            const { data } = await Http.instance.patch(`/bo/products/${id}`, {
                logisticConfigurationSlug
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async updateProductsCollections(
        { commit, dispatch },
        { collectionsSlugs, productId }
    ) {
        try {
            await Http.instance.put(`/bo/products/${productId}/collections`, {
                collectionsSlugs
            });
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async updateProductsFiles({ commit, dispatch }, { filesIds, productId }) {
        try {
            await Http.instance.put(`/bo/products/${productId}/files`, {
                filesIds
            });
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async updateProductLine({ commit, dispatch }, { lineSlug, productId }) {
        try {
            await Http.instance.put(`/bo/products/${productId}/line`, {
                lineSlug
            });
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async updateProductPrices(
        { commit, dispatch },
        { cost, id, price, priceOffer }
    ) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            await Http.instance.put(`/bo/products/${id}/prices`, {
                cost,
                price,
                priceOffer
            });
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async publishProduct({ commit, dispatch }, { active, id, weight }) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            const { data } = await Http.instance.put(
                `/bo/products/${id}/publish`,
                {
                    active,
                    weight
                }
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async publishMassiveProducts({ commit, dispatch }, { products }) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            const { data } = await Http.instance.post(
                "/bo/products/massive/publish",
                {
                    products
                }
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async deleteProduct({ commit, dispatch }, { id }) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            await Http.instance.delete(`/bo/products/${id}`);
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async deleteProductDefinitively({ commit, dispatch }, { id }) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            await Http.instance.delete(`/bo/products/${id}/definitive`);
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async createReference(
        { commit, dispatch },
        {
            alwaysStock,
            barcode,
            color,
            colorRef,
            customReferenceValue,
            images,
            minUnitsForPriceWholesale,
            price,
            priceOffer,
            priceWholesale,
            productId,
            size,
            stock
        }
    ) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            const { data } = await Http.instance.post("/bo/references", {
                alwaysStock,
                barcode,
                color,
                colorRef,
                customReferenceValue,
                images,
                minUnitsForPriceWholesale,
                price,
                priceOffer,
                priceWholesale,
                productId,
                size,
                stock
            });
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async updateReference(
        { commit, dispatch },
        {
            active,
            alwaysStock,
            barcode,
            color,
            colorRef,
            cost,
            customReferenceValue,
            id,
            images,
            minUnitsForPriceWholesale,
            price,
            priceOffer,
            priceWholesale,
            size,
            stock
        }
    ) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            const { data } = await Http.instance.patch(`/bo/references/${id}`, {
                active,
                alwaysStock,
                barcode,
                color,
                colorRef,
                cost,
                customReferenceValue,
                images,
                minUnitsForPriceWholesale,
                price,
                priceOffer,
                priceWholesale,
                size,
                stock
            });

            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    // sucursales-inventario-multitenant: el stock de una referencia solo se
    // guarda por aquí (modo de inventario global); `PATCH /bo/references/:id`
    // ya no lo escribe.
    async updateMultipleReferences(
        { commit, dispatch },
        {
            alwaysStock,
            color,
            colorRef,
            cost,
            id,
            images,
            minUnitsForPriceWholesale,
            price,
            priceOffer,
            priceWholesale,
            size
        }
    ) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            const { data } = await Http.instance.patch(
                `/bo/products/${id}/multiple-references`,
                {
                    alwaysStock,
                    color,
                    colorRef,
                    cost,
                    images,
                    minUnitsForPriceWholesale,
                    price,
                    priceWholesale,
                    priceOffer,
                    size
                }
            );

            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async syncStockReference({ commit, dispatch }, { id }) {
        try {
            const { data } = await Http.instance.post(
                "/bo/references/sync-stock",
                {
                    id
                }
            );

            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async setDefaultReference(
        { commit, dispatch },
        { productId, referenceId }
    ) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            await Http.instance.put("/bo/references/default", {
                productId,
                referenceId
            });
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async deleteReference({ commit, dispatch }, { referenceId }) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            await Http.instance.delete(`/bo/references/${referenceId}`);
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async listReferenceByBarcode({ commit, dispatch }, { barcode }) {
        try {
            const { data } = await Http.instance.get(
                `/bo/references/barcode/${barcode}`
            );
            let references = data.items;
            references = references.map((item) => ({
                ...item.product,
                productId: item.product.id,
                ...formatReference(item)
            }));
            return references;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async listHighlightReferences({ commit, dispatch }, { lineSlug, type }) {
        try {
            const { data } = await Http.instance.get(
                "/bo/references/highlight/",
                {
                    params: {
                        type,
                        lineSlug
                    }
                }
            );
            return data.items;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async updateProductsWeights({ commit, dispatch }, { products, reorder }) {
        commit("tenants/CLEAR_CATALOG_PREVIEW", null, { root: true });
        try {
            await Http.instance.put("/bo/products/massive/weights", {
                products,
                reorder
            });
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    },
    async updateHighlightReferences(
        { commit, dispatch },
        { lineSlug, referencesIds, type }
    ) {
        try {
            const { data } = await Http.instance.put(
                "/bo/references/highlight/",
                {
                    lineSlug,
                    type,
                    referencesIds
                }
            );
            return data;
        } catch (errorAxios) {
            const parseError = handleErrorAxios(errorAxios, dispatch);
            commit("SET_ERROR_PRODUCTS", parseError);
            throw parseError;
        }
    }
};

export default actions;
